/* eslint-disable @typescript-eslint/no-explicit-any */
import { isArray } from "lodash-es";
import { CombinedLabeling, convertJsonObjectToLabeling, convertJsonObjectToWorkspaceLabeling, WorkspaceLabeling } from '../store/appVersion/labeling';
import { convertJsonObjectToLicenseStatuses, LicenseStatus } from '../store/license/license-status';
import { convertContouringJsonObjectToViewModels, convertContouringViewModelsToJson, convertSingleModelJsonToContouringOutput } from '../store/contouring/contouring-helpers';
import { ContouringCustomizationEntities, ContouringCustomizationEntitiesForExport, ContouringCustomizationOutputEntities } from '../store/contouring/contouring-types';
import { convertJsonObjectToMVisionClientList, MVisionAppClient } from '../store/configurationTarget/mvision-client-list';
import store from '../store/store';
import { JSONstringifyOrder } from '../util/string-convert';
import BackendApiInterface from './backend-api-interface';
import { SaveResult } from './save-response';
import { convertDaemonConfigToJson, convertJsonObjectToDaemonConfig, convertJsonObjectToDaemonConfigs, DaemonConfig, DaemonCredentials, DaemonCredentialsSchema } from '../store/daemon/daemon-config';
import { AccessRights, parseAccessRights } from "../store/user/access-rights";
import { appConfigSelectors } from "../store/appConfig/appConfigSlice";
import { getBackendApi } from "../app-config";
import { convertDoseJsonObjectToViewModels, convertDoseViewModelsToJson, convertSingleModelJsonToDoseOutput } from "../store/dose/dose-helpers";
import { DoseCustomizationEntities, DoseCustomizationEntitiesForExport, DoseCustomizationOutputEntities } from "../store/dose/dose-types";
import { ModelType } from "../store/global-types/customization-types";
import { convertImageJsonObjectToViewModels, convertImageViewModelsToJson, convertSingleModelJsonToImageOutput } from "../store/image/image-helpers";
import { ImageCustomizationEntities, ImageCustomizationEntitiesForExport, ImageCustomizationOutputEntities } from "../store/image/image-types";
import { convertAdaptJsonObjectToViewModels, convertAdaptViewModelsToJson, convertSingleModelJsonToAdaptOutput } from "../store/adapt/adapt-helpers";
import { AdaptCustomizationEntities, AdaptCustomizationEntitiesForExport, AdaptCustomizationOutputEntities } from "../store/adapt/adapt-types";
import { BackupInfo } from "../store/backup/backup-types";



export type ModelsList = {
    availableModels: string[];
    deprecatedModels: string[];
}

/** Extracts the config data from a backup download response, throwing if missing. */
const extractBackupConfigData = (response: any): unknown => {
    const configData = response?.backup_config_data;
    if (configData == null || (typeof configData === 'object' && Object.keys(configData).length === 0)) {
        throw new Error('Backup response missing config data');
    }
    return configData;
};

/** Converts a ModelType enum into the string used by the backup API endpoints. */
const convertModelTypeToBackupApiString = (modelType: ModelType): string => {
    switch (modelType) {
        case ModelType.Contouring: return 'contouring';
        case ModelType.Dose: return 'dose';
        case ModelType.Image: return 'translation';
        case ModelType.Adapt: return 'registration';
        default: throw new Error(`Unsupported model type for backup API: ${modelType}`);
    }
};

/**
 * API wrapper for accessing backend's configuration API funtions. This class contains generic wrapper code that will,
 * in turn, call current correct implementation of the backend API client (cloud, local, dev-time, etc).
 */
export default class ConfigurationClient {

    /** Returns true if the deployment config forces all saves to use the force flag. */
    private get shouldForceSave(): boolean {
        const deploymentInfo = appConfigSelectors.selectAppDeploymentInfo(store.getState());
        return deploymentInfo?.alwaysForceSave === true;
    }

    /** Gets a backend client for any backend API calls matching whatever is the current
     * cloud backend.
     */
    private get api(): BackendApiInterface {
        const appConfig = appConfigSelectors.selectAppConfig(store.getState());
        if (appConfig === undefined) {
            throw new Error(`Could not get current app config.`);
        }

        return getBackendApi(appConfig);
    }

    /** Fetch ALL contouring model customizations. */
    public async fetchContouringCustomizationsAsync(appClient: MVisionAppClient | undefined): Promise<ContouringCustomizationEntities> {
        const jsonContents = await this.api.fetchContouringCustomizationAsync(appClient);
        const customizations = convertContouringJsonObjectToViewModels(jsonContents);
        return customizations;
    }

    /** Save the entire contouring customization config to backend in one go */
    public async saveContouringCustomizationAsync(appClient: MVisionAppClient | undefined, modelCustomization: ContouringCustomizationEntitiesForExport, force?: boolean): Promise<SaveResult> {
        const jsonObject = convertContouringViewModelsToJson(modelCustomization);
        const json = JSONstringifyOrder(jsonObject);
        const result = await this.api.saveContouringCustomizationAsync(appClient, json, force || this.shouldForceSave);
        return result;
    }

    /** Deletes ALL contouring customizations for a specific app client which should revert them to default factory settings. */
    public async deleteContouringCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        const success = await this.api.deleteContouringCustomizationAsync(appClient);
        return success;
    }

    /** Fetches default output settings for a contouring model */
    public async fetchDefaultConfigurationForContouringOutput(modelName: string): Promise<ContouringCustomizationOutputEntities> {
        const jsonContents = await this.api.fetchDefaultConfigurationForContouringModelAsync(modelName);
        const customization = convertSingleModelJsonToContouringOutput(jsonContents);
        return customization;
    }

    /** Fetch ALL dose model customizations. */
    public async fetchDoseCustomizationsAsync(appClient: MVisionAppClient | undefined): Promise<DoseCustomizationEntities> {
        const jsonContents = await this.api.fetchDoseCustomizationAsync(appClient);
        const customizations = convertDoseJsonObjectToViewModels(jsonContents);
        return customizations;
    }

    /** Save the entire dose customization config to backend in one go */
    public async saveDoseCustomizationAsync(appClient: MVisionAppClient | undefined, modelCustomization: DoseCustomizationEntitiesForExport, force?: boolean): Promise<SaveResult> {
        const jsonObject = convertDoseViewModelsToJson(modelCustomization);
        const json = JSONstringifyOrder(jsonObject);
        const result = await this.api.saveDoseCustomizationAsync(appClient, json, force || this.shouldForceSave);
        return result;
    }

    /** Deletes ALL dose customizations for a specific app client which should revert them to default factory settings. */
    public async deleteDoseCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        const success = await this.api.deleteDoseCustomizationAsync(appClient);
        return success;
    }

    /** Fetches default output settings for a dose model */
    public async fetchDefaultConfigurationForDoseOutput(modelName: string): Promise<DoseCustomizationOutputEntities> {
        const jsonContents = await this.api.fetchDefaultConfigurationForDoseModelAsync(modelName);
        const customization = convertSingleModelJsonToDoseOutput(jsonContents);
        return customization;
    }

    /** Fetch ALL adapt+ model customizations. */
    public async fetchAdaptCustomizationsAsync(appClient: MVisionAppClient | undefined): Promise<AdaptCustomizationEntities> {
        const jsonContents = await this.api.fetchAdaptCustomizationAsync(appClient);
        const customizations = convertAdaptJsonObjectToViewModels(jsonContents);
        return customizations;
    }

    /** Save the entire adapt+ customization config to backend in one go */
    public async saveAdaptCustomizationAsync(appClient: MVisionAppClient | undefined, modelCustomization: AdaptCustomizationEntitiesForExport, force?: boolean): Promise<SaveResult> {
        const jsonObject = convertAdaptViewModelsToJson(modelCustomization);
        const json = JSONstringifyOrder(jsonObject);
        const result = await this.api.saveAdaptCustomizationAsync(appClient, json, force || this.shouldForceSave);
        return result;
    }

    /** Deletes ALL adapt customizations for a specific app client which should revert them to default factory settings. */
    public async deleteAdaptCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        const success = await this.api.deleteAdaptCustomizationAsync(appClient);
        return success;
    }

    /** Fetches default output settings for an image model */
    public async fetchDefaultConfigurationForImageOutput(modelName: string): Promise<ImageCustomizationOutputEntities> {
        const jsonContents = await this.api.fetchDefaultConfigurationForImageModelAsync(modelName);
        const customization = convertSingleModelJsonToImageOutput(jsonContents);
        return customization;
    }

    /** Fetch ALL image+ model customizations. */
    public async fetchImageCustomizationsAsync(appClient: MVisionAppClient | undefined): Promise<ImageCustomizationEntities> {
        const jsonContents = await this.api.fetchImageCustomizationAsync(appClient);
        const customizations = convertImageJsonObjectToViewModels(jsonContents);
        return customizations;
    }

    /** Save the entire image+ customization config to backend in one go */
    public async saveImageCustomizationAsync(appClient: MVisionAppClient | undefined, modelCustomization: ImageCustomizationEntitiesForExport, force?: boolean): Promise<SaveResult> {
        const jsonObject = convertImageViewModelsToJson(modelCustomization);
        const json = JSONstringifyOrder(jsonObject);
        const result = await this.api.saveImageCustomizationAsync(appClient, json, force || this.shouldForceSave);
        return result;
    }

    /** Deletes ALL image customizations for a specific app client which should revert them to default factory settings. */
    public async deleteImageCustomizationAsync(appClient: MVisionAppClient | undefined): Promise<boolean> {
        const success = await this.api.deleteImageCustomizationAsync(appClient);
        return success;
    }

    /** Fetches default output settings for an adapt model */
    public async fetchDefaultConfigurationForAdaptOutput(modelName: string): Promise<AdaptCustomizationOutputEntities> {
        const jsonContents = await this.api.fetchDefaultConfigurationForAdaptModelAsync(modelName);
        const customization = convertSingleModelJsonToAdaptOutput(jsonContents);
        return customization;
    }

    /** Returns an array of all the model names that are available for current logged-in user (as per their auth),
      * or undefined if the list could not be retrieved correctly.
      */
    public async fetchAvailableModelsAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<ModelsList | undefined> {
        const activeModelsList = await this.api.fetchActiveModelsAsync(appClient, modelType);
        const deprecatedModels: string[] = [];

        if ('user_model_details' in activeModelsList && isArray(activeModelsList['user_model_details'])) {
            const availableModels: string[] = [];
            for (const model of activeModelsList['user_model_details']) {
                if ('model_name' in model) {
                    if (model['deprecated']) {
                        deprecatedModels.push(model['model_name']);
                    }
                    availableModels.push(model['model_name']);
                }
            }
            return { availableModels, deprecatedModels };
        }

        return undefined;
    }

    /** Fetch list of configuration backups for a given model type. */
    public async fetchBackupsAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<BackupInfo[]> {
        return await this.api.fetchBackupsAsync(appClient, convertModelTypeToBackupApiString(modelType));
    }

    /** Download a contouring configuration backup and convert to typed entities. */
    public async downloadContouringBackupAsync(appClient: MVisionAppClient | undefined, backupFilename: string): Promise<ContouringCustomizationEntities> {
        const response = await this.api.downloadBackupAsync(appClient, convertModelTypeToBackupApiString(ModelType.Contouring), backupFilename);
        const configData = extractBackupConfigData(response);
        return convertContouringJsonObjectToViewModels(configData);
    }

    /** Download a dose configuration backup and convert to typed entities. */
    public async downloadDoseBackupAsync(appClient: MVisionAppClient | undefined, backupFilename: string): Promise<DoseCustomizationEntities> {
        const response = await this.api.downloadBackupAsync(appClient, convertModelTypeToBackupApiString(ModelType.Dose), backupFilename);
        const configData = extractBackupConfigData(response);
        return convertDoseJsonObjectToViewModels(configData);
    }

    /** Download an image configuration backup and convert to typed entities. */
    public async downloadImageBackupAsync(appClient: MVisionAppClient | undefined, backupFilename: string): Promise<ImageCustomizationEntities> {
        const response = await this.api.downloadBackupAsync(appClient, convertModelTypeToBackupApiString(ModelType.Image), backupFilename);
        const configData = extractBackupConfigData(response);
        return convertImageJsonObjectToViewModels(configData);
    }

    /** Download an adapt configuration backup and convert to typed entities. */
    public async downloadAdaptBackupAsync(appClient: MVisionAppClient | undefined, backupFilename: string): Promise<AdaptCustomizationEntities> {
        const response = await this.api.downloadBackupAsync(appClient, convertModelTypeToBackupApiString(ModelType.Adapt), backupFilename);
        const configData = extractBackupConfigData(response);
        return convertAdaptJsonObjectToViewModels(configData);
    }

    public async fetchLicenseStatusAsync(appClient: MVisionAppClient | undefined): Promise<LicenseStatus[]> {
        const jsonContents: any = await this.api.fetchLicenseStatusAsync(appClient);
        const licenseStatuses = convertJsonObjectToLicenseStatuses(jsonContents);
        return licenseStatuses;
    }

    public async saveLicenseAsync(appClient: MVisionAppClient | undefined, newLicense: string): Promise<boolean> {
        const success = await this.api.saveLicenseAsync(appClient, newLicense);

        return success;
    }

    /** Fetches all daemon configurations for a specific app client. */
    public async fetchDaemonConfigsAsync(appClient: MVisionAppClient | undefined): Promise<DaemonConfig[]> {
        const jsonContents: any = await this.api.fetchAllDaemonConfigsAsync(appClient);
        const daemonConfigs = convertJsonObjectToDaemonConfigs(jsonContents);
        return daemonConfigs;
    }

    /** Fetches a single daemon config or undefined. */
    public async fetchDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonClientId: string): Promise<DaemonConfig> {
        const json: any = await this.api.fetchDaemonConfigAsync(appClient, daemonClientId);
        const daemonConfig = convertJsonObjectToDaemonConfig(json);
        return daemonConfig;
    }

    public async regenerateCredentialsAsync(appClient: MVisionAppClient | undefined, sessionID: string): Promise<DaemonCredentials> {
        const json: any = await this.api.regenerateCredentialsAsync(appClient, sessionID);
        const result = DaemonCredentialsSchema.safeParse(json);

        if (result.success) {
            return result.data;
        } else {
            return { key: '', secret: '' };
        }
    }

    public async tokenAssociationApproveAsync(appClient: MVisionAppClient | undefined, token: string, sessionID: string) {
        await this.api.tokenAssociationApproveAsync(appClient, token, sessionID);
    }

    /** Saves a single daemon configuration. */
    public async saveDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig): Promise<boolean> {
        const jsonObject = convertDaemonConfigToJson(daemonConfig);
        const json = JSONstringifyOrder(jsonObject);
        const success = await this.api.saveDaemonConfigAsync(appClient, json);
        return success;
    }

    /** Resets a specific daemon configuration of a specific app client to default factory settings. */
    public async resetDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig): Promise<boolean> {
        const success = await this.api.resetDaemonConfigAsync(appClient, daemonConfig.sessionId);
        return success;
    }

    /** Deletes a specific daemon configuration of a specific app client. */
    public async deleteDaemonConfigAsync(appClient: MVisionAppClient | undefined, daemonConfig: DaemonConfig): Promise<boolean> {
        const success = await this.api.deleteDaemonConfigAsync(appClient, daemonConfig.sessionId);
        return success;
    }

    public async fetchLabelingAsync(modelTypes: ModelType[]): Promise<CombinedLabeling> {
        const labelJson = await this.api.fetchLabelingAsync(modelTypes);

        const labeling = convertJsonObjectToLabeling(labelJson.labeling, labelJson.backendVersion);
        return labeling;
    }

    public async fetchWorkspaceLabelingAsync(): Promise<WorkspaceLabeling> {
        const labelJson = await this.api.fetchWorkspaceLabelingAsync();
        const labeling = convertJsonObjectToWorkspaceLabeling(labelJson);
        return labeling;
    }

    public async fetchClientListAsync(applicationsOnly?: boolean): Promise<MVisionAppClient[]> {
        const clientListJsonContents = await this.api.fetchClientListAsync(applicationsOnly);

        const clientList = convertJsonObjectToMVisionClientList(clientListJsonContents);
        return clientList;
    }

    public async exportCustomizationPdfAsync(appClient: MVisionAppClient | undefined, modelType: ModelType): Promise<Blob> {
        return await this.api.exportCustomizationPdfAsync(appClient, modelType);
    }

    public async fetchAndParseAccessRights(): Promise<AccessRights> {
        const accessRightsJSON = await this.api.fetchAccessRightsAsync();
        const parsedAccessRights = parseAccessRights(accessRightsJSON);
        return parsedAccessRights;
    };


    public async fetchUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string): Promise<any> {
        return await this.api.getUserSettingAsync(appClient, settingName);
    }

    public async saveUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string, value: any): Promise<boolean> {
        return await this.api.saveUserSettingAsync(appClient, settingName, JSON.stringify(value));
    }

    public async deleteUserSettingAsync(appClient: MVisionAppClient | undefined, settingName: string): Promise<boolean> {
        return await this.api.deleteUserSettingAsync(appClient, settingName);
    }
}
